// src/components/engine/ProtectionModeSelector.jsx
// §4.1 Protection Mode Selector
//
// Radio-группа: Stable | Balanced | Aggressive
// GET/POST /api/engine/mode

import { useState, useEffect } from "react";
import { workerClient, BASE_URL } from "../../api/workerClient";

const COLORS = {
  stable:     "#12cc76",
  balanced:   "#f5a623",
  aggressive: "#e85555",
};

const ICONS = {
  stable:     "🛡",
  balanced:   "⚖",
  aggressive: "⚡",
};

export default function ProtectionModeSelector() {
  const [current,  setCurrent]  = useState(null);
  const [applying, setApplying] = useState(false);
  const [error,    setError]    = useState(null);
  const [descs,    setDescs]    = useState({});

  useEffect(() => {
    workerClient("/api/engine/mode")
      .then(res => {
        if (res.ok) {
          setCurrent(res.mode);
          setDescs(res.descriptions ?? {});
        }
      })
      .catch(() => {});
  }, []);

  async function applyMode(mode) {
    if (mode === current || applying) return;
    setApplying(true);
    setError(null);

    try {
      const res = await fetch(`${BASE_URL}/api/engine/mode`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ mode }),
      }).then(r => r.json());

      if (res.ok) setCurrent(mode);
      else        setError(res.error ?? "Failed to apply mode");
    } catch {
      setError("Network error");
    } finally {
      setApplying(false);
    }
  }

  const modes = ["stable", "balanced", "aggressive"];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      <div style={{
        fontFamily:    "var(--font-mono)",
        fontSize:      9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color:         "var(--text-muted)",
        marginBottom:  4,
      }}>
        Protection Mode
      </div>

      {modes.map(mode => {
        const isActive = current === mode;
        const color    = COLORS[mode];
        const desc     = descs[mode];

        return (
          <button
            key={mode}
            onClick={() => applyMode(mode)}
            disabled={applying}
            style={{
              display:      "flex",
              alignItems:   "flex-start",
              gap:          14,
              padding:      "14px 18px",
              borderRadius: "var(--radius)",
              background:   isActive ? `${color}10` : "var(--bg-card)",
              border:       `1px solid ${isActive ? color : "var(--border-subtle)"}`,
              cursor:       applying ? "wait" : "pointer",
              textAlign:    "left",
              transition:   "all 0.15s",
              width:        "100%",
            }}
          >
            {/* Radio dot */}
            <div style={{
              width:        16, height: 16, borderRadius: "50%",
              border:       `2px solid ${isActive ? color : "var(--border)"}`,
              flexShrink:   0, marginTop: 2,
              background:   isActive ? color : "transparent",
              boxShadow:    isActive ? `0 0 8px ${color}60` : "none",
              transition:   "all 0.15s",
            }} />

            <div style={{ flex: 1 }}>
              <div style={{
                display:       "flex",
                alignItems:    "center",
                gap:           8,
                marginBottom:  4,
              }}>
                <span>{ICONS[mode]}</span>
                <span style={{
                  fontFamily:  "var(--font-mono)",
                  fontSize:    13, fontWeight: 700,
                  color:       isActive ? color : "var(--text-primary)",
                  textTransform: "capitalize",
                }}>
                  {desc?.label ?? mode}
                </span>
              </div>
              <div style={{
                fontFamily: "var(--font-mono)",
                fontSize:   11,
                color:      "var(--text-muted)",
                lineHeight: 1.55,
              }}>
                {desc?.description ?? ""}
              </div>
            </div>
          </button>
        );
      })}

      {applying && (
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 10,
          color:      "var(--text-muted)", textAlign: "center",
        }}>
          Applying…
        </div>
      )}

      {error && (
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 10,
          color:      "var(--danger)", textAlign: "center",
        }}>
          {error}
        </div>
      )}

    </div>
  );
}
